import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../apis/baseQuery';
import { API_CONSTANTS } from '../../../utils/constants';
import dashboardApi from '../../dashboard/dashboardApi';

// Re-export model types so consumers can import from usersApi or model directly
export type { User as AppUser } from './model';
export type { UserListRequest } from './model';
export type { UserListResponse, UserListMeta } from './model';
export type { UserDetailResponse, UserDetail } from './model';

import type {
  UserListRequest,
  UserListResponse,
  UserDetailResponse,
} from './model';

// ─── RTK Query API slice ─────────────────────────────────────────────────────────
export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: ['UserList', 'UserDetail'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // GET /api/v1/admin/users  (with query params)
    getUserList: builder.query<UserListResponse, UserListRequest>({
      query: (params = {}) => ({
        url: API_CONSTANTS.USERS.USER_LIST_URL,
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'UserList' as const,
                id,
              })),
              { type: 'UserList', id: 'LIST' },
            ]
          : [{ type: 'UserList', id: 'LIST' }],
    }),

    // GET /api/v1/admin/users/deleted
    getDeletedUserList: builder.query<UserListResponse, UserListRequest>({
      query: (params = {}) => ({
        url: API_CONSTANTS.USERS.USER_DELETED_LIST_URL,
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'UserList' as const,
                id,
              })),
              { type: 'UserList', id: 'DELETED_LIST' },
            ]
          : [{ type: 'UserList', id: 'DELETED_LIST' }],
    }),

    // GET /api/v1/admin/users/detail/:userId
    getUserDetail: builder.query<
      UserDetailResponse,
      { userId: string; param?: string }
    >({
      query: ({ userId, param }) => ({
        url: API_CONSTANTS.USERS.USER_DETAIL_URL(userId),
        method: 'GET',
        params: param ? { param } : undefined,
      }),
      providesTags: (_result, _error, { userId, param }) => [
        { type: 'UserDetail', id: userId },
        ...(param ? [{ type: 'UserDetail', id: `${userId}-${param}` }] : []),
      ],
    }),
    // POST /api/v1/admin/users/change-status
    changeUserStatus: builder.mutation<
      { settings: { success: boolean; message: string } },
      { id: string; status: 'active' | 'inactive' }
    >({
      query: (data) => ({
        url: API_CONSTANTS.USERS.USER_CHANGE_STATUS_URL,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id: userId }) => [
        { type: 'UserList', id: 'LIST' },
        { type: 'UserList', id: userId },
        { type: 'UserDetail', id: userId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            dashboardApi.util.invalidateTags([
              { type: 'DashboardKpi', id: 'SUMMARY' },
            ])
          );
        } catch {
          // Keep dashboard cache untouched if the status update fails.
        }
      },
    }),
    resendUserVerificationEmail: builder.mutation<
      { success: boolean; message: string },
      { id: string }
    >({
      query: (data) => ({
        url: API_CONSTANTS.USERS.USER_RESEND_VERIFICATION_URL,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetUserListQuery,
  useLazyGetUserListQuery,
  useGetDeletedUserListQuery,
  useLazyGetDeletedUserListQuery,
  useGetUserDetailQuery,
  useLazyGetUserDetailQuery,
  useChangeUserStatusMutation,
  useResendUserVerificationEmailMutation,
} = usersApi;

export default usersApi;
